/**
 * MainCanvas — React wrapper around MainScene (T-1.1.3) + drop target for
 * the ShapePanel drag source (T-1.3.2 ghost preview).
 *
 * - Creates a <canvas> DOM node.
 * - useEffect: instantiates MainScene, enables OrbitControls, starts the
 *   render loop, and constructs a GhostPreview bound to the live scene.
 * - useEffect cleanup: disposes the ghost, then dispose()s MainScene.
 * - Wraps the <canvas> in a div that listens for HTML5 drag events:
 *     dragenter / dragover → show ghost at the snapped grid cell. From
 *                              T-1.3.4 onwards the ghost colour reflects
 *                              conflict status (blue = free, red = the
 *                              voxels Map already contains this coord).
 *     dragleave / drop      → hide ghost. Drop does NOT place a voxel
 *                              yet — PlaceVoxel lands in T-1.3.5.
 *
 * Why the ghost ref lives inside this effect (Constitution §2.3 / T-1.1.5
 * hand-off):
 *   - "不持有 MainScene ref" means *external* UI must not grab MainScene.
 *     MainCanvas itself owns it (and now the GhostPreview), which keeps
 *     App.tsx ignorant of 3D plumbing.
 *   - Toolbar already uses the same pattern: onSceneMount callback
 *     hands the scene up to App only for resetView. GhostPreview is
 *     internal-only — App never sees it.
 *
 * Why this is T-1.3.2 → T-1.3.3 → T-1.3.4:
 *   - T-1.3.2 ships the wiring + visual contract against the simplest
 *     possible raycast target (the floor plane, y=0).
 *   - T-1.3.3 wires the Raycaster through pickDropCoord, which adds:
 *       (a) voxel upward-face hit → stack-on-top semantics (y+1)
 *       (b) fallback to the ground plane y=0 when no voxel is hit
 *       (c) null on complete miss → ghost.hide() (T-1.3.2 bug #1 fix:
 *           ghost no longer sticks at a stale coord when the pointer
 *           leaves the canvas)
 *   - T-1.3.4 (this revision) introduces conflict detection:
 *       - drop handler reads the voxels Map from useSceneStore.getState()
 *         and asks checkConflict(coord, voxels) whether the target cell
 *         is already occupied.
 *       - occupied → ghost.showConflict(coord) (red, 0xFF4444).
 *       - free     → ghost.show(coord) / ghost.update(coord) (blue,
 *                    default 0x4A90E2).
 *       - the conflict check is a *view-layer hint*, not a gate. PlaceVoxel
 *         (T-1.3.5) decides policy; the user can still drop on red.
 *   - T-1.3.5 will replace drop = hide with drop = PlaceVoxel. The drop
 *     handler is in place; only its body changes. T-1.3.5 also fills
 *     `voxelMeshes` from sceneStore; until then the array is empty.
 */

import { useEffect, useRef } from 'react';
import { Raycaster } from 'three';
import { MainScene } from './MainScene';
import { GhostPreview } from './GhostPreview';
import { pickAirCoord } from './pickAirCoord';
// pickDropCoord is intentionally NOT imported here: T-1.4.1 routes
// every pointer → coord through pickAirCoord (M1 MVP). When M2 adds
// 'move' / 'select' tools, pickCoord becomes a switch over ui.tool
// and pickDropCoord returns to this file.
import { checkConflict } from './checkConflict';
import { placeVoxelAt } from '@/commands/placeVoxelAt';
import { pushUndo } from '@/commands/Command';
import { useSceneStore } from '@/state/sceneStore';
import { SHAPE_DRAG_MIME, type ShapeDragPayload } from '@/ui/ShapePanel';
import type { VoxelCoord } from '@/state/types';

export interface MainCanvasProps {
  className?: string;
  /** Optional className on the wrapping div. */
  wrapperClassName?: string;
  /**
   * Called once after the underlying MainScene is constructed (and again
   * with `null` on unmount). Lets the parent hold a ref to the live scene
   * so UI controls (e.g. Toolbar "重置视角" — T-1.1.5) can call back into
   * the 3D layer without going through forwardRef ceremony.
   */
  onSceneMount?: (scene: MainScene | null) => void;
}

export function MainCanvas({
  className,
  wrapperClassName,
  onSceneMount,
}: MainCanvasProps): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const sceneRef = useRef<MainScene | null>(null);
  const ghostRef = useRef<GhostPreview | null>(null);
  // T-1.3.3 — single Raycaster instance reused across pointer events.
  // pickDropCoord mutates it via setFromCamera; we own the lifetime.
  const raycasterRef = useRef<Raycaster | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const parent = canvas.parentElement;

    const fitSize = (): { width: number; height: number } => {
      const width = parent?.clientWidth ?? window.innerWidth;
      const height = parent?.clientHeight ?? window.innerHeight;
      return { width, height };
    };

    const { width, height } = fitSize();
    canvas.width = width;
    canvas.height = height;

    const scene = new MainScene(canvas);
    sceneRef.current = scene;
    onSceneMount?.(scene);

    // T-1.3.2 — bind a GhostPreview to the live scene. App.tsx never
    // sees this ref; the ghost is purely an internal preview artefact.
    ghostRef.current = new GhostPreview(scene.getScene());
    // T-1.3.3 — bind a Raycaster for drag-time hit tests. Allocated
    // here so MainCanvas owns its lifetime (one per mount, freed on
    // unmount via the ref-clear below).
    raycasterRef.current = new Raycaster();

    // ResizeObserver keeps the canvas in sync with layout. Falls back to
    // window resize when ResizeObserver is unavailable.
    const onResize = (): void => {
      const { width: w, height: h } = fitSize();
      canvas.width = w;
      canvas.height = h;
      const camera = scene.getCamera();
      const renderer = scene.getRenderer();
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h, false);
    };

    // initial size after construction
    onResize();

    scene.enableOrbitControls();
    // T-1.1.4 — add the 16×16 grid floor so the empty scene is not pitch
    // black. Default size + unit spacing match PRD FR-101.
    scene.addGridFloor();
    scene.startRenderLoop();

    let observer: ResizeObserver | null = null;
    if (parent && typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(onResize);
      observer.observe(parent);
    } else {
      window.addEventListener('resize', onResize);
    }

    return () => {
      if (observer) {
        observer.disconnect();
      } else {
        window.removeEventListener('resize', onResize);
      }
      // T-1.3.2 — dispose the ghost before disposing the scene. After
      // this point `ghostRef.current` must be cleared so any late drag
      // event (e.g. drop after unmount) is a no-op.
      ghostRef.current?.hide();
      ghostRef.current = null;
      // T-1.3.3 — drop the raycaster ref too. Raycaster has no GPU
      // resources, but clearing the ref means any post-unmount handler
      // that somehow fires will see `null` and bail out cleanly.
      raycasterRef.current = null;
      // T-1.1.4 — detach the grid floor before disposing the scene. removeGridFloor
      // is a no-op if no grid is attached, so this is safe to call on every
      // unmount regardless of mount-time add order.
      scene.removeGridFloor();
      scene.dispose();
      sceneRef.current = null;
      onSceneMount?.(null);
    };
    // Effect runs once on mount, cleans up on unmount. We intentionally
    // omit `onSceneMount` from deps: the contract is "App passes a
    // useCallback-stabilised handler" (see App.tsx handleSceneMount).
    // Listing it here would re-create the entire 3D scene whenever the
    // callback identity changes — a footgun for downstream tasks that
    // might pass an inline lambda by accident.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----- drag-and-drop handlers (T-1.3.2) -----

  /**
   * Parse the ShapePanel drag payload from dataTransfer. Returns null if
   * the drag does not carry a shape payload (e.g. a stray file drop or
   * a text selection). Defensive — the drop handler in T-1.3.5 will
   * share this parser.
   */
  const readShapePayload = (dt: DataTransfer | null): ShapeDragPayload | null => {
    if (!dt) {
      return null;
    }
    const raw = dt.getData(SHAPE_DRAG_MIME);
    if (!raw) {
      return null;
    }
    try {
      const parsed = JSON.parse(raw) as ShapeDragPayload;
      // shape: 'cube' is the only contract today; anything else is
      // unknown to T-1.3.2 and should be ignored silently so a future
      // drag source doesn't crash the canvas.
      if (parsed && typeof parsed.shape === 'string') {
        return parsed;
      }
      return null;
    } catch {
      return null;
    }
  };

  /**
   * Convert a pointer position (clientX/Y) to NDC and ask pickAirCoord
   * for a snapped voxel coord. Returns null on complete miss (pointer
   * outside canvas, ray parallel to all four planes).
   *
   * T-1.4.1 (M1 收尾): pickAirCoord replaces pickDropCoord here.
   *   - pickAirCoord walks y=0,1,2,3 horizontal planes (no scene
   *     walk, no voxel-mesh hit-test) so the user can place voxels
   *     floating / cantilevered in the air (PRD FR-103).
   *   - The current MVP only exposes ui.tool === 'place', so every
   *     pointer → coord resolution routes through pickAirCoord.
   *   - When M2 adds tools like 'move' or 'select', this helper
   *     becomes a `switch (tool)` dispatcher that routes to
   *     pickDropCoord (move/select) or pickAirCoord (place).
   *   - The four-plane walker means pickAirCoord does NOT need the
   *     scene or voxelMeshes, so this helper drops those params
   *     relative to the T-1.3.3 signature.
   */
  const pickCoord = (clientX: number, clientY: number) => {
    const canvas = canvasRef.current;
    const raycaster = raycasterRef.current;
    const scene = sceneRef.current;
    if (!canvas || !raycaster || !scene) {
      return null;
    }
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      return null;
    }
    const ndcX = ((clientX - rect.left) / rect.width) * 2 - 1;
    const ndcY = -((clientY - rect.top) / rect.height) * 2 + 1;
    // T-1.4.1 — pickAirCoord is scene-free. It only needs the
    // raycaster, camera, and NDC; it walks four planes (y=0..3)
    // and snaps to the lowest-y hit. No voxelMeshes plumbing.
    return pickAirCoord(raycaster, scene.getCamera(), ndcX, ndcY);
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>): void => {
    const payload = readShapePayload(e.dataTransfer);
    if (!payload) {
      return;
    }
    e.preventDefault();
    const ghost = ghostRef.current;
    if (!ghost) {
      return;
    }
    const coord = pickCoord(e.clientX, e.clientY);
    if (!coord) {
      // T-1.3.3 — null ⇒ pointer is outside the canvas / ray misses
      // everything. We still keep the drag session alive (don't hide
      // here — dragenter fires once; the continuous dragover handler
      // owns the "hide on miss" responsibility).
      return;
    }
    // T-1.3.4 — route through checkConflict so the ghost colour tells
    // the user whether the drop target is occupied. showConflict/show are
    // idempotent so it's fine to call either on dragenter.
    if (checkConflict(coord, useSceneStore.getState().voxels)) {
      ghost.showConflict(coord);
    } else {
      ghost.show(coord);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
    const payload = readShapePayload(e.dataTransfer);
    if (!payload) {
      return;
    }
    // preventDefault is REQUIRED on dragover — without it, the browser
    // refuses to fire the drop event. We do it before raycasting so the
    // gesture remains a valid drop target even if the ray misses.
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    const ghost = ghostRef.current;
    if (!ghost) {
      return;
    }
    const coord = pickCoord(e.clientX, e.clientY);
    if (!coord) {
      // T-1.3.3 — ray missed everything (pointer off-canvas). Hide
      // the ghost instead of letting it sit at a stale coord (T-1.3.2
      // self-reported bug #1).
      ghost.hide();
      return;
    }
    // T-1.3.4 — re-check conflict on every dragover because the user
    // can drag from a free cell into an occupied one (or vice versa)
    // without leaving the canvas. update()/showConflict() reposition the
    // existing mesh; create-once invariant holds.
    if (checkConflict(coord, useSceneStore.getState().voxels)) {
      ghost.showConflict(coord);
    } else {
      ghost.update(coord);
    }
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>): void => {
    const payload = readShapePayload(e.dataTransfer);
    if (!payload) {
      return;
    }
    e.preventDefault();
    ghostRef.current?.hide();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>): void => {
    const payload = readShapePayload(e.dataTransfer);
    if (!payload) {
      return;
    }
    e.preventDefault();

    // T-1.3.5 → T-1.3.6 — turn the drop into a Command. We:
    //   1. Read the current voxels from the live store.
    //   2. Conflict-check (view-layer hint from T-1.3.4). On conflict
    //      placeVoxelAt returns null and we bail out — the user can
    //      retry on a free cell. (T-1.3.5 overwrote on red; T-1.3.6
    //      tightens to reject because drop and click now share this
    //      helper, and silent overwrites from a click would be a worse
    //      surprise than from a deliberate drag-and-drop.)
    //   3. Build a NEW Map with the voxel added, hand it to setVoxels
    //      so immer produces a new draft and subscribers re-render.
    //      We deliberately do NOT mutate `state.voxels` in place:
    //      that would skip immer's structural-sharing copy and the
    //      ghost / any voxel-mesh subscribers would not see the change.
    //   4. Push the PlaceVoxel command onto undoStack via pushUndo,
    //      which also clears the redoStack.
    //
    // Why build a fresh Map instead of calling cmd.apply() inside the
    // immer producer: the Command interface accepts the *narrow*
    // `{ voxels }` shape (see Command.ts), and the existing setVoxels
    // setter is what triggers a structural-share update. We therefore
    // apply the command on a local clone (mirroring what immer would
    // do internally), then hand the new Map to setVoxels. The command
    // itself stays as a pure data-plane record on the undo stack —
    // replaying it on undo/redo uses the same code path.
    //
    // UI never mutates state directly — Constitution §2.1: the
    // handler asks the store + Command layer to do the work.
    const coord = pickCoord(e.clientX, e.clientY);
    if (!coord) {
      ghostRef.current?.hide();
      return;
    }
    // Touch checkConflict so the read is intentional (not just
    // discarded) — if M2 introduces a "replace existing" policy, the
    // branch is already at the right place. Today: drop on red
    // returns null from placeVoxelAt and we no-op.
    void checkConflict(coord, useSceneStore.getState().voxels);

    placeVoxelAtIntoStore(coord);

    // T-1.3.6 — dragend usually fires a synthetic click right after.
    // Suppress that click for a short window so a single drag doesn't
    // place two voxels (one via drop, one via the trailing click).
    suppressNextClick();

    ghostRef.current?.hide();
  };

  /**
   * T-1.3.6 — Shared "place a voxel at coord" path used by both drop
   * and click handlers. Delegates to `placeVoxelAt` for the conflict
   * check + Command construction, then commits to the live store +
   * undo stack exactly the way T-1.3.5 wired the drop handler.
   *
   * Returns true if a voxel was placed, false if the coord was a
   * conflict / null. Callers can use the boolean to decide whether to
   * clear or refresh any visual hint (drop uses it to bail; click uses
   * it to skip ghost reset).
   */
  const placeVoxelAtIntoStore = (coord: VoxelCoord): boolean => {
    const cmd = placeVoxelAt(coord, useSceneStore.getState().voxels);
    if (!cmd) {
      return false;
    }
    // Apply on a fresh Map clone, then hand it to setVoxels so immer
    // produces a new draft + subscribers see the change. PlaceVoxel.apply
    // narrows to `{ voxels }` (the Command interface accepts the
    // smallest structural shape it needs; PlaceVoxel only touches
    // voxels, so we satisfy that here).
    const next = new Map(useSceneStore.getState().voxels);
    cmd.apply({ voxels: next });
    useSceneStore.getState().setVoxels(next);
    pushUndo(useSceneStore.getState(), cmd);
    return true;
  };

  /**
   * T-1.3.6 — Drop a voxel via plain click on the canvas. Reuses the
   * shared handlePlace path (placeVoxelAtIntoStore) so click + drop
   * share identical policy (conflict check, props generation, undo
   * recording).
   *
   * Click suppression: HTML5 dragend fires a synthetic `click` on the
   * drop target immediately after. If we don't swallow it, a single
   * drag places two voxels (one via drop, one via click). We mark a
   * short suppress window from the drop handler; this click handler
   * checks the flag and bails out.
   */
  const suppressNextClickRef = useRef<number>(0);
  const suppressNextClick = (): void => {
    suppressNextClickRef.current = Date.now() + 250; // 250ms window
  };
  const isClickSuppressed = (): boolean => Date.now() < suppressNextClickRef.current;

  const handleClick = (e: React.MouseEvent<HTMLCanvasElement>): void => {
    // T-1.3.6 — respect the drop→click suppression window.
    if (isClickSuppressed()) {
      return;
    }
    // T-1.3.6 — read live tool; only 'place' is actionable in M1.
    // Other tools (move/select/paint/label/unfold) are M2 and the
    // click here is a no-op for them.
    const tool = useSceneStore.getState().ui.tool;
    if (tool !== 'place') {
      return;
    }
    const coord = pickCoord(e.clientX, e.clientY);
    if (!coord) {
      return;
    }
    placeVoxelAtIntoStore(coord);
  };

  return (
    <div
      className={wrapperClassName}
      style={{ width: '100%', height: '100%', position: 'relative' }}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <canvas
        ref={canvasRef}
        className={className}
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
          touchAction: 'none',
        }}
        aria-label="3D scene canvas"
        onClick={handleClick}
      />
    </div>
  );
}
